// Responsive <picture> wrapper (plan 21).
//
// Takes the slug of an image under `public/images/` (generated by
// `scripts/optimize-images.ts`) and renders a WebP + JPEG srcset at
// 480w / 768w / 1280w:
//
//   <ResponsiveImage src="hero-hummingbird" alt="…" loading="eager" fetchPriority="high" />
//
// Above-the-fold hero images should pass `loading="eager"` and
// `fetchPriority="high"`; below-the-fold ones get the defaults
// (`loading="lazy"`, no fetchPriority).
//
// The pipeline emits:
//   /images/<slug>-480w.webp
//   /images/<slug>-768w.webp
//   /images/<slug>-1280w.webp
//   /images/<slug>-480w.jpg
//   /images/<slug>-768w.jpg
//   /images/<slug>-1280w.jpg
//
// The `<img>` fallback points at the 1280w jpg — any browser without
// srcset support still gets a readable (~80-150 KB) JPEG.

import type { ImgHTMLAttributes } from 'react';

export type ResponsiveImageFetchPriority = 'high' | 'low' | 'auto';

export interface ResponsiveImageProps extends Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  'src' | 'srcSet' | 'fetchPriority'
> {
  /** Base slug under /images/, without extension or size suffix. */
  src: string;
  /** Alt text. Required — pass an empty string for decorative images. */
  alt: string;
  /** `sizes` attribute; defaults to a reasonable full-width breakpoint. */
  sizes?: string;
  /** `loading`; defaults to `lazy`. Above-the-fold images should pass `eager`. */
  loading?: 'lazy' | 'eager';
  /** Hints the browser's fetch priority. */
  fetchPriority?: ResponsiveImageFetchPriority;
  /**
   * `className` is forwarded to BOTH the wrapping `<picture>` and the
   * inner `<img>` so absolutely-positioned / sized containers work
   * without a flex-busting extra div. Pass `imgClassName` if you need to
   * target only the `<img>`.
   */
  imgClassName?: string;
}

const WIDTHS = [480, 768, 1280] as const;

// An absolute base (admin-uploaded product image, migration 063) is a full URL
// like `https://<proj>.supabase.co/storage/v1/object/public/product-images/products/<uuid>`
// or the dev `/media/<bucket>/products/<uuid>` path. The upload pipeline writes
// WebP-only variants at `<base>-NNNw.webp`, so there's no JPEG fallback track.
// A relative base is a bare slug under `public/images/` (build-time pipeline)
// with both WebP and JPEG variants.
function isAbsoluteBase(src: string): boolean {
  return /^https?:\/\//i.test(src) || src.startsWith('/media/');
}

function buildSrcSet(base: string, ext: 'webp' | 'jpg', absolute: boolean): string {
  return WIDTHS.map((w) =>
    absolute ? `${base}-${w}w.${ext} ${w}w` : `/images/${base}-${w}w.${ext} ${w}w`
  ).join(', ');
}

export const ResponsiveImage = ({
  src,
  alt,
  sizes = '(min-width: 1024px) 1280px, (min-width: 768px) 768px, 100vw',
  loading = 'lazy',
  fetchPriority,
  className,
  imgClassName,
  ...rest
}: ResponsiveImageProps) => {
  const absolute = isAbsoluteBase(src);
  const webpSrcSet = buildSrcSet(src, 'webp', absolute);
  // Absolute (uploaded) bases are WebP-only; relative slugs also have JPEGs.
  const jpgSrcSet = absolute ? null : buildSrcSet(src, 'jpg', absolute);
  const fallback = absolute ? `${src}-1280w.webp` : `/images/${src}-1280w.jpg`;

  // React 19 accepts `fetchPriority` as a camelCase prop on img.
  // boundary: `fetchPriority` is not yet typed on ImgHTMLAttributes<T>.
  const priorityAttr = fetchPriority ? { fetchPriority } : {};

  return (
    <picture className={className}>
      <source type="image/webp" srcSet={webpSrcSet} sizes={sizes} />
      {jpgSrcSet && <source type="image/jpeg" srcSet={jpgSrcSet} sizes={sizes} />}
      <img
        src={fallback}
        alt={alt}
        loading={loading}
        decoding="async"
        className={imgClassName ?? className}
        {...priorityAttr}
        {...rest}
      />
    </picture>
  );
};
